import React, { useEffect, useRef } from "react";

//Common
import { CheckCircleFill } from "react-bootstrap-icons";

//App
import { EmployeePledgeSummaryProps, PaymentOption } from "../../../types";
import { getFormattedCurrency, getLookupValue } from "../../../utility/helpers";
import {
  AGENCY_OTHER_OPTION_ID,
  DATA_PRIVACY_TEXT,
  GIFT_INVESTMENT_OPTION_OTHER_ID,
  TAX_TEXT,
  TAX_TEXT_STYLE,
  TAX_TEXT_CONFIRMATION_STYLE,
} from "./constants";
import usePledgeStyles from "./styles";
import { getFormattedDate } from "../../../utility/lib/date";
import { useReactToPrint } from "react-to-print";
import PrintPledgeSummary from "./PrintPledgeSummary";

const EmployeePledgeSummary: React.FC<EmployeePledgeSummaryProps> = (props) => {
  const { summary, lookups, isPledgeSuccess, isConfirmation, onPrintReady } = props;
  const {
    contributionAmount,
    paymentOptionId,
    isNotWillingToContribute,
    organizationContactName,
    organizationContactEmail,
    submitDate,
    payrollDeductionFrequency,
    spouseName,
    spousePledgeAmount,
    spouseWorkPlace,
    isWillingToStayAnonymous,
    isWillingToCombineSpouseContribution,
    giftInvestment,
    isRecurringContribution,
  } = summary;

  const summaryStyles = usePledgeStyles();
  const {
    paymentOption,
    agencies,
    giftInvestment: giftInvestmentLookup,
  } = lookups ?? {};

  const paymentOptionValue = getLookupValue(paymentOptionId, paymentOption);

  const hasPaymentOption = paymentOptionId && paymentOptionValue;

  const isPayrollDeduction =
    hasPaymentOption && paymentOptionId === PaymentOption.PayrollDeduction;

  const isCreditCard =
    hasPaymentOption && paymentOptionId === PaymentOption.CreditCard;

  const isCheck = hasPaymentOption && paymentOptionId === PaymentOption.Check;

  let methodOfGiving = null;
  if (hasPaymentOption) {
    methodOfGiving = paymentOptionValue;
    if (isPayrollDeduction && payrollDeductionFrequency) {
      methodOfGiving += ` - ${payrollDeductionFrequency}`;
    } else if (isCreditCard && contributionAmount && isRecurringContribution) {
      methodOfGiving += `: 12 monthly payments of ${getFormattedCurrency(
        Number((contributionAmount / 12).toFixed(2))
      )}`; //Bug-15950
    } else if (isCheck) {
      methodOfGiving +=
        " - Capital Area United Way, 700 Laurel Street, Baton Rouge, LA 70802";
    }
  }

  const hasSpouseContribution =
    isWillingToCombineSpouseContribution &&
    spouseName &&
    spouseWorkPlace &&
    spousePledgeAmount;

  const agenciesList = giftInvestment?.map((x) => {
    if (x.giftInvestmentId === GIFT_INVESTMENT_OPTION_OTHER_ID) {
      return {
        name:
          x.agencyId === AGENCY_OTHER_OPTION_ID
            ? x.otherAgencyName
            : getLookupValue(x.agencyId, agencies),
        amount: x.pledgeAmount,
      };
    } else {
      return {
        name: getLookupValue(x.giftInvestmentId, giftInvestmentLookup),
        amount: x.pledgeAmount,
      };
    }
  });

  const EMAIL_CAMPAIGN_REPRESENTATIVE_TEXT = (
    <p>
      If any of this information is incorrect, please email your campaign
      representative {organizationContactName} at{" "}
      <a href={`mailto:${organizationContactEmail}`}>
        {organizationContactEmail}
      </a>
    </p>
  );

  const showEmailCampaignRepresentativeText =
    organizationContactName && organizationContactEmail;

  const formattedContributionAmount = contributionAmount
    ? getFormattedCurrency(contributionAmount)
    : "$0";

  const detailRow = (label: string, value: React.ReactNode) => (
    <div style={summaryStyles.detailItem}>
      <span style={summaryStyles.detailLabel}>{label}</span>
      <span style={summaryStyles.detailValue}>{value}</span>
    </div>
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const printSummary = useReactToPrint({ contentRef });

  useEffect(() => {
    onPrintReady?.(printSummary);
  }, [onPrintReady, printSummary]);

  return (
    <main style={summaryStyles.summaryShell}>
      <div>
        {!isConfirmation && (
          <header style={summaryStyles.summaryHero}>
            <div style={summaryStyles.successIcon} aria-hidden="true">
              <CheckCircleFill />
            </div>
            <p style={summaryStyles.summaryEyebrow}>
              {isPledgeSuccess ? "Pledge confirmed" : "Pledge summary"}
            </p>
            {!isNotWillingToContribute ? (
              <>
                <h1 style={summaryStyles.summaryTitle}>
                  {"Thank you for your support"}
                </h1>
                <p style={summaryStyles.summarySubtitle}>
                  Your pledge to the Exxon Donor Campaign has been recorded. Together, we are helping our community thrive.
                </p>
              </>
            ) : (
              <>
                <h1 style={summaryStyles.summaryTitle}>Thank you for your response</h1>
                <p style={summaryStyles.summarySubtitle}>Your response has been recorded successfully.</p>
              </>
            )}
          </header>
        )}

        <section style={summaryStyles.summaryCard} aria-labelledby="pledge-details-heading">
          <div style={summaryStyles.summaryCardHeader}>
            <h2 id="pledge-details-heading" style={summaryStyles.summaryCardTitle}>Pledge details</h2>
            <span style={summaryStyles.amountBadge}>{isNotWillingToContribute ? "$0" : formattedContributionAmount}</span>
          </div>
          <div style={summaryStyles.detailsGrid}>
          {!isNotWillingToContribute ? (
            <>
            {submitDate && detailRow("Pledge date", getFormattedDate(submitDate.toString()))}
            {detailRow("Pledge amount", formattedContributionAmount)}
            {hasPaymentOption && detailRow("Method of giving", methodOfGiving)}
            {detailRow("Anonymous donation", isWillingToStayAnonymous?.toString() === "1" ? "Yes" : "No")}

            {agenciesList && agenciesList?.length > 0 && (
              <div style={summaryStyles.designationList}>
                <span style={summaryStyles.detailLabel}>Designations</span>
                <div>
                  {agenciesList
                    ?.filter((x) => x?.amount !== 0)
                    ?.map((agency, index) => (
                      <div key={index} style={summaryStyles.designationItem}>
                        <span style={summaryStyles.designationName}>{agency.name}</span>
                        <span style={summaryStyles.designationAmount}>{agency.amount ? getFormattedCurrency(agency.amount) : "$0"}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {hasSpouseContribution && (
              detailRow("Spouse / significant other", `${spouseName} — ${spouseWorkPlace}: ${spousePledgeAmount ? getFormattedCurrency(spousePledgeAmount) : "$0"}`)
            )}
            </>
          ) : (
            <>
              {detailRow("Response", "I do not wish to pledge or contribute at this time.")}
              {detailRow("Pledge amount", "$0")}
            </>
          )}
          </div>
        </section>

        {showEmailCampaignRepresentativeText && (
          <div style={summaryStyles.contactCallout}>
            {EMAIL_CAMPAIGN_REPRESENTATIVE_TEXT}
          </div>
        )}

        <div
          style={
            isConfirmation
              ? { ...TAX_TEXT_CONFIRMATION_STYLE, ...summaryStyles.summaryFooter }
              : { ...TAX_TEXT_STYLE, ...summaryStyles.summaryFooter }
          }
        >
          {TAX_TEXT}
          <br />
          <br />
          {DATA_PRIVACY_TEXT}
        </div>
      </div>
      <div style={{ display: "none" }}>
        <PrintPledgeSummary
          ref={contentRef}
          summary={summary}
          lookups={lookups}
        />
      </div>
    </main>
  );
};

export default EmployeePledgeSummary;
